import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Channel, handleAvatarError } from '../../types';
import { resolveAvatarUrl } from '../../utils/serverUrl';
import { matchesChannelQuery } from '../../utils/channelSearch';
import { useChannelMemberIndex } from '../../hooks/useChannelMemberIndex';
import { ChannelTypeIcon, channelDisplayName } from '../ChannelListItem';
import { Modal } from '../Modal';
import { Pin, Search, X } from 'lucide-react';

/**
 * 홈 즐겨찾기 '추가' — 대화방을 골라 바로 즐겨찾기(=채팅방 고정)에 올리고 내린다.
 * 예전엔 '추가'가 대화방 탭으로 보내기만 해서, 거기서 방을 다시 길게 누르거나 우클릭해야 했다.
 *
 * - 즐겨찾기는 사람이 아니라 '고정한 대화방'이다. 사람 이름으로 찾으면 그 사람과의 1:1 방과
 *   그 사람이 있는 방이 나온다(검색 규칙은 대화방 목록·전달 창과 같은 matchesChannelQuery 하나).
 *   대화한 적 없는 동료를 위해 빈 1:1 방을 만들지 않는다 — 상대 목록에 빈 방이 생긴다(초안 DM 설계).
 * - 행을 누르는 즉시 App.handleTogglePin(낙관적 반영)이 돈다. 뒤의 홈 칩이 바로 바뀌는 것이 확인이다.
 * - ⚠ #root로 포털한다. 모바일 홈은 PullToRefresh(absolute z-10, 당길 때 transform) 안에 있어
 *   그 안에 그리면 fixed 모달이 탭바 아래로 깔리거나 따라 움직인다(ContextMenu와 같은 이유).
 * - HomeScreen이 열 때만 마운트한다 — 열 때마다 검색어가 비어 있다.
 */

const portalHost = () => document.getElementById('root') ?? document.body;

interface PinPickerModalProps {
  channels: Channel[];
  onTogglePin: (channel: Channel) => void;
  onClose: () => void;
}

export const PinPickerModal: React.FC<PinPickerModalProps> = ({ channels, onTogglePin, onClose }) => {
  const [query, setQuery] = useState('');
  // 참여자 이름 검색용 — 대화방 목록·전달 창과 모듈 캐시를 공유해 실제 요청은 세션당 1회다
  const memberIndex = useChannelMemberIndex(true);

  // 홈 즐겨찾기(HomeScreen pinnedChannels)와 같은 대상 — 보관된 방과 '나와의 채팅'은 칩이 되지 않는다
  const rooms = useMemo(
    () => channels.filter(ch => !ch.isArchived && ch.type !== 'self'),
    [channels]
  );
  const pinnedCount = useMemo(() => rooms.filter(ch => ch.isPinned).length, [rooms]);

  // 최신 대화순. 고정을 바꿔도 순서가 변하지 않아 누른 행이 손가락 밑에서 움직이지 않는다
  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rooms
      .filter(ch => matchesChannelQuery(ch, q, memberIndex))
      .sort((a, b) => (b.lastMessageAt || '').localeCompare(a.lastMessageAt || ''));
  }, [rooms, query, memberIndex]);

  const footer = (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-slate-500">즐겨찾기 {pinnedCount}개</span>
      <button
        type="button"
        onClick={onClose}
        className="h-10 px-5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold transition-colors"
      >
        완료
      </button>
    </div>
  );

  return createPortal(
    <Modal isOpen onClose={onClose} title="즐겨찾기 추가" footer={footer} bodyFlex>
      <div className="relative shrink-0">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" aria-hidden="true" />
        {/* type="search"는 크롬 기본 지우기(×)가 아래 버튼과 겹쳐 두 개가 된다 — 전달 창과 같은 text */}
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="이름, 참여자, 설명으로 찾기"
          aria-label="이름, 참여자, 설명으로 대화방 찾기"
          className="w-full pl-9 pr-9 py-2 bg-slate-100 rounded-lg text-base md:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-400"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery('')}
            aria-label="검색어 지우기"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
          >
            <X size={14} aria-hidden="true" />
          </button>
        )}
      </div>

      {/* 행이 모달 가장자리까지 닿게 본문 좌우 여백(px-6)만큼 넓힌다 */}
      <div className="-mx-6 mt-3 min-h-0 overflow-y-auto border-t border-slate-100">
        {candidates.length === 0 ? (
          <div className="px-6 py-10 text-center">
            {rooms.length === 0 ? (
              <>
                <p className="text-sm font-medium text-slate-600">즐겨찾기할 대화방이 없습니다</p>
                <p className="mt-1 text-xs text-slate-400">동료를 찾아 첫 대화를 시작해 보세요</p>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-slate-600">검색 결과가 없습니다</p>
                <p className="mt-1 text-xs text-slate-400">다른 이름으로 찾아보세요</p>
              </>
            )}
          </div>
        ) : (
          candidates.map(ch => {
            const pinned = !!ch.isPinned;
            const name = channelDisplayName(ch);
            return (
              <button
                key={ch.id}
                type="button"
                onClick={() => onTogglePin(ch)}
                aria-pressed={pinned}
                aria-label={`${name} 즐겨찾기`}
                className="group w-full flex items-center gap-3 px-6 py-2.5 text-left hover:bg-slate-50 transition-colors"
              >
                {ch.type === 'dm' && ch.dmPartnerAvatar ? (
                  <img
                    src={resolveAvatarUrl(ch.dmPartnerAvatar)} onError={handleAvatarError}
                    alt=""
                    className="w-9 h-9 rounded-full object-cover shrink-0 bg-slate-100"
                  />
                ) : (
                  <ChannelTypeIcon type={ch.type} size="sm" />
                )}
                <span className="flex-1 min-w-0 text-sm text-slate-800 truncate">{name}</span>
                <Pin
                  size={18}
                  aria-hidden="true"
                  fill={pinned ? 'currentColor' : 'none'}
                  className={`shrink-0 transition-colors ${
                    pinned ? 'text-brand-600' : 'text-slate-300 group-hover:text-slate-400'
                  }`}
                />
              </button>
            );
          })
        )}
      </div>
    </Modal>,
    portalHost()
  );
};
